import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "../lib/auth";
import api from "../lib/api";
import { Sparkles, ArrowLeft, Calendar, Users, MapPin, Activity } from "lucide-react";
import { Button } from "../components/ui/button";
import HealthScore from "../components/HealthScore";
import BPMChart from "../components/BPMChart";
import KPIDashboard from "../components/KPIDashboard";
import ConfidenceBadge from "../components/ConfidenceBadge";

function pickTitle(a, locale) {
  if (!a) return "";
  if (locale === "en") return a.title_en || a.title_fr || a.id;
  return a.title_fr || a.title_en || a.id;
}

function signalLabel(sig, groupeLocale) {
  if (!sig) return "";
  if (groupeLocale === "en") return sig.label_en || sig.label_fr || sig.code;
  return sig.label_fr || sig.label_en || sig.code;
}

export default function GroupePage() {
  const { groupeId } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const [data, setData] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const [{ data: g }, { data: h }] = await Promise.all([
          api.get(`/groupes/${groupeId}`),
          api.get(`/groupes/${groupeId}/historique`),
        ]);
        setData(g);
        setHistory(h);
      } catch {
        // toast already shown by interceptor : group page degrades gracefully
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [groupeId]);

  const uiLocale = (i18n.language || "fr").startsWith("en") ? "en" : "fr";
  const dateLocale = uiLocale === "en" ? "en-GB" : "fr-FR";

  if (loading) return <div className="space-y-4 animate-pulse" data-testid="group-loading">
    <div className="h-32 bg-bg-soft rounded-2xl" />
    <div className="grid grid-cols-2 gap-4">
      <div className="h-60 bg-bg-soft rounded-xl" />
      <div className="h-60 bg-bg-soft rounded-xl" />
    </div>
  </div>;

  if (!data) return <div className="text-center text-text-secondary">{uiLocale === "en" ? "Group not found" : "Groupe introuvable"}</div>;

  const { group, members_count, bpm_distribution, latest_kpi, kpi_history, kpi_6w, kpi_window, last_diagnostic, active_prescriptions } = data;
  const canDiagnose = ["dr", "dz", "dn", "ceo"].includes(user.role);
  const displayKpi = kpi_6w || latest_kpi;
  const previousKpi = kpi_history?.[3];
  const score = last_diagnostic?.sante_globale;
  // Diagnostic content is in the group's language (independent of UI).
  const groupeLocale = last_diagnostic?.groupe_locale || group?.langue || "fr";

  return (
    <div className="space-y-6" data-testid="group-page">
      <Link to="/" className="inline-flex items-center gap-1.5 text-sm text-text-secondary hover:text-apollo-orange-dark" data-testid="back-link">
        <ArrowLeft className="w-4 h-4" /> {t("group.back_to_dashboard")}
      </Link>

      {/* Block 1: Identity */}
      <section className="bg-white border border-border rounded-2xl p-6 md:p-8" data-testid="group-identity">
        <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
          <div>
            <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-apollo-orange-dark font-bold mb-2">
              <MapPin className="w-3.5 h-3.5" /> {group.ville} · {group.region}
            </div>
            <h1 className="font-heading font-black text-3xl md:text-5xl tracking-tighter text-text-primary">{group.nom}</h1>
            <div className="flex flex-wrap items-center gap-4 mt-3 text-sm text-text-secondary">
              <span className="inline-flex items-center gap-1.5"><Users className="w-4 h-4" /> {members_count} {t("group.members").toLowerCase()}</span>
              <span className="inline-flex items-center gap-1.5"><Calendar className="w-4 h-4" /> {t("group.since")} {new Date(group.date_creation).toLocaleDateString(dateLocale)}</span>
              <span className="px-2 py-0.5 rounded-full bg-apollo-orange-light text-apollo-orange-dark text-xs font-bold">{t(`phase.${group.phase}`, group.phase)}</span>
            </div>
          </div>
          {score !== undefined && <HealthScore value={score} testId="group-health-score" />}
        </div>
      </section>

      {/* Block 2 + 3: BPM + KPI */}
      <section className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="bg-white border border-border rounded-xl p-6" data-testid="bpm-section">
          <h2 className="font-heading font-bold text-lg mb-3">{t("group.bpm_title")} (DISC)</h2>
          <BPMChart distribution={bpm_distribution} />
          {bpm_distribution ? (
            <p className="text-sm text-text-secondary mt-3 font-oracle italic">
              {(() => {
                const di = (bpm_distribution.D || 0) + (bpm_distribution.I || 0);
                const sc = (bpm_distribution.S || 0) + (bpm_distribution.C || 0);
                if (uiLocale === "en") {
                  if (di > 65) return `"Strong D+I dominance (${di}%) : risk of impulsive decisions, lack of analytics."`;
                  if (sc > 60) return `"Strong S+C dominance (${sc}%) : risk of slowness, lack of speed."`;
                  return `"Balanced composition : fertile ground for deep-dive workshops."`;
                }
                if (di > 65) return `« Dominante D+I forte (${di}%) : risque d'emballement, manque d'analytique. »`;
                if (sc > 60) return `« Dominante S+C forte (${sc}%) : risque de lenteur, manque de vitesse. »`;
                return `« Composition équilibrée : terrain propice à des ateliers d'approfondissement. »`;
              })()}
            </p>
          ) : (
            <p className="text-sm text-text-secondary mt-3 italic">{t("group.bpm_no_data")}</p>
          )}
        </div>

        <div className="bg-white border border-border rounded-xl p-6" data-testid="kpi-section">
          <h2 className="font-heading font-bold text-lg mb-3">{t("group.kpi_title")}</h2>
          <KPIDashboard latest={displayKpi} previous={previousKpi} window={kpi_window} />
        </div>
      </section>

      {/* Block 4: Diagnostic */}
      <section className="bg-gradient-to-br from-apollo-orange-light/40 via-white to-bg-soft border border-border rounded-2xl p-6 md:p-8" data-testid="diagnostic-section">
        {!last_diagnostic ? (
          <div className="text-center py-6">
            <Sparkles className="w-12 h-12 text-apollo-orange mx-auto mb-4" />
            <h2 className="font-heading font-black text-2xl tracking-tight mb-2">{t("group.no_diagnostic")}</h2>
            <p className="text-text-secondary mb-6 max-w-xl mx-auto font-oracle italic text-lg">
              « {uiLocale === "en"
                  ? "No recent diagnostic for this group. Run the oracle to reveal symptoms and prescriptions."
                  : "Aucun diagnostic récent pour ce groupe. Lancez l'oracle pour révéler symptômes et prescriptions."} »
            </p>
            {canDiagnose && (
              <Button
                onClick={() => navigate(`/groupe/${groupeId}/diagnostic`)}
                className="bg-apollo-orange hover:bg-apollo-orange-dark text-white h-12 px-6 text-base font-semibold"
                data-testid="cta-lancer-diagnostic"
              >
                <Sparkles className="w-5 h-5 mr-2" /> {t("group.run_diagnostic")}
              </Button>
            )}
          </div>
        ) : (
          <div>
            <div className="flex items-start justify-between gap-4 mb-4">
              <div>
                <div className="text-xs uppercase tracking-widest text-apollo-orange-dark font-bold mb-1">
                  {t("group.last_diagnostic")} · {new Date(last_diagnostic.generated_at).toLocaleDateString(dateLocale)}
                </div>
                {(last_diagnostic.is_stale || String(last_diagnostic.model_used || "").startsWith("stub")) && (
                  <div className="flex flex-wrap gap-2 mb-2">
                    {last_diagnostic.is_stale && (
                      <span className="text-xs font-bold px-2.5 py-0.5 rounded-full bg-amber-100 text-amber-700 border border-amber-300" data-testid="diag-stale-badge">
                        {t("group.stale_badge")}
                      </span>
                    )}
                    {String(last_diagnostic.model_used || "").startsWith("stub") && (
                      <span className="text-xs font-bold px-2.5 py-0.5 rounded-full bg-bg-soft text-text-secondary border border-border" data-testid="diag-degraded-badge">
                        {t("group.degraded_badge")}
                      </span>
                    )}
                  </div>
                )}
                <h2 className="font-heading font-black text-2xl tracking-tight">{last_diagnostic.etat_synthese}</h2>
              </div>
              {canDiagnose && (
                <Button onClick={() => navigate(`/groupe/${groupeId}/diagnostic`)} variant="outline" className="border-apollo-orange/30 text-apollo-orange-dark" data-testid="btn-nouveau-diagnostic">
                  <Sparkles className="w-4 h-4 mr-2" /> {t("nav.new")}
                </Button>
              )}
            </div>
            <p className="font-oracle italic text-lg text-text-primary leading-relaxed">
              « {last_diagnostic.note_strategique} »
            </p>
            <div className="flex flex-wrap gap-2 mt-4">
              {last_diagnostic.symptomes_detectes?.slice(0, 6).map((s, i) => (
                <span key={s.code || `sym-${i}`} className="text-xs font-medium px-2 py-1 rounded-full bg-white border border-border text-text-secondary" data-testid={`group-symptom-${s.code}`}>
                  {signalLabel(s, groupeLocale)} <span className="text-text-secondary/60">· {t(`severity.${s.severite === "forte" ? "high" : s.severite === "moyenne" ? "medium" : "low"}`, s.severite)}</span>
                </span>
              ))}
            </div>
          </div>
        )}
      </section>

      {/* Block 5: Prescriptions actives */}
      {active_prescriptions?.length > 0 && (
        <section className="bg-white border border-border rounded-xl p-6" data-testid="active-prescriptions-section">
          <h2 className="font-heading font-bold text-xl tracking-tight mb-4">{t("group.active_prescriptions")}</h2>
          <div className="space-y-2">
            {active_prescriptions.map((p, i) => (
              <PrescriptionRow key={p.id} prescription={p} index={i} groupeId={groupeId} canMark={canDiagnose} groupeLocale={groupeLocale} />
            ))}
          </div>
        </section>
      )}

      {/* Block 6: Timeline */}
      {history.length > 0 && (
        <section className="bg-white border border-border rounded-xl p-6" data-testid="history-timeline">
          <h2 className="font-heading font-bold text-xl tracking-tight mb-4 flex items-center gap-2">
            <Activity className="w-5 h-5 text-apollo-orange" /> {t("group.history")}
          </h2>
          <div className="relative pl-8">
            <div className="absolute left-3 top-0 bottom-0 w-0.5 bg-border" />
            {history.map((h) => (
              <div key={h.id} className="relative mb-4 last:mb-0" data-testid={`history-item-${h.id}`}>
                <div className="absolute -left-[1.4rem] top-1.5 w-3 h-3 rounded-full bg-apollo-orange ring-4 ring-apollo-orange-light" />
                <div className="flex items-center justify-between gap-2">
                  <div>
                    <div className="text-xs text-text-secondary">{new Date(h.realise_le).toLocaleDateString(dateLocale)}</div>
                    <div className="font-medium text-text-primary">
                      <span className="font-mono text-xs text-text-secondary mr-2">{h.atelier_id}</span>
                      {pickTitle(h.atelier, uiLocale)}
                    </div>
                  </div>
                  {h.feedback_score && (
                    <span className="text-xs font-bold px-2 py-0.5 rounded-full bg-apollo-orange-light text-apollo-orange-dark">
                      {h.feedback_score}/5
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}

function PrescriptionRow({ prescription, index, groupeId, canMark, groupeLocale }) {
  const { t, i18n } = useTranslation();
  const [atelier, setAtelier] = useState(null);
  const [marking, setMarking] = useState(false);
  const uiLocale = (i18n.language || "fr").startsWith("en") ? "en" : "fr";

  useEffect(() => {
    api.get(`/ateliers/${prescription.atelier_id}`).then((r) => setAtelier(r.data)).catch(() => {});
  }, [prescription.atelier_id]);

  const mark = async () => {
    setMarking(true);
    try {
      await api.post("/ateliers-realises", {
        atelier_id: prescription.atelier_id,
        groupe_id: groupeId,
        realise_le: new Date().toISOString().slice(0, 10),
        prescription_id: prescription.id,
        feedback_score: 4,
      });
      window.location.reload();
    } catch (e) {
      alert(e?.response?.data?.detail || (uiLocale === "en" ? "Error" : "Erreur"));
    } finally {
      setMarking(false);
    }
  };

  return (
    <div className="flex items-center justify-between gap-3 p-3 bg-bg-soft rounded-lg" data-testid={`prescription-row-${prescription.id}`}>
      <div className="flex items-center gap-3 min-w-0">
        <span className="font-mono font-bold text-xs px-2 py-1 rounded bg-apollo-orange text-white">#{index + 1}</span>
        <span className="font-mono text-xs px-2 py-0.5 rounded bg-white border border-border">{prescription.atelier_id}</span>
        <ConfidenceBadge confidence={prescription.confidence} compact />
        <div className="min-w-0">
          {/* Workshop title in the GROUP's language (it's part of the diagnostic). */}
          <div className="font-medium text-sm truncate">{pickTitle(atelier, groupeLocale)}</div>
          <div className="text-xs text-text-secondary truncate">{prescription.raisonnement?.slice(0, 90)}…</div>
        </div>
      </div>
      <div className="flex items-center gap-2 flex-shrink-0">
        <Link to={`/atelier/${prescription.atelier_id}`}>
          <Button variant="ghost" size="sm" data-testid={`btn-voir-atelier-${prescription.atelier_id}`}>{t("group.view_workshop")}</Button>
        </Link>
        {canMark && (
          <Button size="sm" onClick={mark} disabled={marking} className="bg-apollo-orange hover:bg-apollo-orange-dark text-white" data-testid={`btn-mark-fait-${prescription.id}`}>
            {marking ? t("group.marking") : t("group.mark_done")}
          </Button>
        )}
      </div>
    </div>
  );
}
